import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Button,
    CircularProgress,
    Alert,
    Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { logout, getLocalToken } from '../utils';

const api = axios.create({
    baseURL: '/admin/api/v1',
});

api.interceptors.request.use((config) => {
    const token = getLocalToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

function DetailItem({ label, value }) {
    return (
        <Box>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    display: 'block',
                    mb: 0.5,
                    letterSpacing: 0.4,
                }}
            >
                {label}
            </Typography>

            <Typography component="div" fontWeight={500}>
                {value || 'N/A'}
            </Typography>
        </Box>
    );
}

export default function EventDetails() {
    const { uid } = useParams();

    const [loading, setLoading] = useState(true);
    const [event, setEvent] = useState(null);

    const [showFullDesc, setShowFullDesc] = useState(false);

    useEffect(() => {
        const fetchEvent = async () => {
            try {
                const res = await api.get(`/events/${uid}`);
                setEvent(res && res.data ? res.data.payload : null);
            } catch (err) {
                console.error(err);
                if (err.response?.status === 401) {
                    logout();
                }
            } finally {
                setLoading(false);
            }
        };
        fetchEvent();
    }, [uid]);

    if (loading) {
        return (
            <Box display="flex" py={10}>
                <CircularProgress />
            </Box>
        );
    }

    if (!event) {
        return <Alert severity="error">Event not found.</Alert>;
    }

    return (
        <Box maxWidth="xl" mx="auto" pb={6}>
            <Box mb={4}>
                <Typography variant="h4" fontWeight={800} gutterBottom>
                    {event.title}
                </Typography>
            </Box>

            <Grid container spacing={3} alignItems="flex-start">
                <Grid size={{ xs: 12, md: 7 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            bgcolor: 'background.paper',
                        }}
                    >
                        <DetailItem
                            label="Description"
                            value={
                                event.description
                                    ? (() => {
                                        const text = event.description.replace(/\\n/g, '\n'); // handle "\n"
                                        const isLong = text.length > 500;

                                        const displayed = showFullDesc || !isLong
                                            ? text
                                            : text.slice(0, 500) + '...';

                                        return (
                                            <Box>
                                                <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {displayed}
                                                </Typography>

                                                {isLong && (
                                                    <Button
                                                        onClick={() => setShowFullDesc(v => !v)}
                                                        startIcon={
                                                            showFullDesc ? <ExpandLessIcon /> : <ExpandMoreIcon />
                                                        }
                                                        sx={{
                                                            mt: 1,
                                                            textTransform: 'none',
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {showFullDesc ? 'Show less description' : 'Show full description'}
                                                    </Button>
                                                )}
                                            </Box>
                                        );
                                    })()
                                    : 'N/A'
                            }
                        />

                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <DetailItem label="Author" value={`${event.firstName} ${event.lastName}`} />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <DetailItem label="Category" value={event.category} />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <DetailItem label="Price" value={event.price || 'Free'} />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <DetailItem label="Event ID" value={event.uid} />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <DetailItem label="Starts At" value={`${event.startsAt ? new Date(event.startsAt).toLocaleString(undefined, { hour12: false }) : 'N/A'}`} />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <DetailItem label="Ends At" value={`${event.endsAt ? new Date(event.endsAt).toLocaleString(undefined, { hour12: false }) : 'N/A'}`} />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Address
                            </Typography>
                            {event.lat != null && event.long != null ? (
                                <a
                                    href={`https://maps.google.com/?q=${event.lat},${event.long}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        color: "#1976d2",
                                        textDecoration: "none",
                                        fontWeight: 500,
                                    }}
                                    title={`Open ${event.location} in Google Maps`}
                                >
                                    <LocationOnIcon fontSize="small" />
                                    <span>{event.location}</span>
                                </a>
                            ) : (
                                <Typography color="text.secondary">
                                    {event.location}
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 4,
                            bgcolor: 'background.paper',
                            position: { md: 'sticky' },
                            top: 24,
                        }}
                    >
                        {event.media?.length ? (
                            <Grid container spacing={2}>
                                {event.media.map((item, index) => (
                                    <Grid key={index} size={{ xs: 12 }}>
                                        <Box
                                            sx={{
                                                borderRadius: 3,
                                                overflow: 'hidden',
                                                bgcolor: 'grey.100',
                                                transition: '0.2s',
                                                '&:hover': {
                                                    transform: 'scale(1.01)',
                                                },
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={item.url}
                                                alt={`Event media ${index + 1}`}
                                                sx={{
                                                    width: '100%',
                                                    height: '30%',
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Typography color="text.secondary">
                                No media available.
                            </Typography>
                        )}
                    </Paper>
                </Grid>
                <Box mt={4}>
                    <Button
                        component={Link}
                        to="/events"
                        startIcon={<ArrowBackIcon />}
                    >
                        Events
                    </Button>
                </Box>
            </Grid>


        </Box>
    );
}